import type {
  ProfileUpdatePayload,
  ProfileUpdateVariables,
} from "./profile.type";
import type { ContextSlug } from "@/entities/context";
import type { ApiError } from "@/services/api";

import {
  useIsMutating,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";

import { client } from "@/config/client";

import {
  CURRENT_USER_PROFILE_QUERY_KEY,
  UPDATE_CONTEXT_MUTATION_KEY,
} from "./profile.constant";

export const useUpdateProfile = () => {
  const queryClient = useQueryClient();

  const mutationFn = (data: ProfileUpdateVariables) => {
    return client.profile.update(data);
  };

  const onSuccess = () => {
    queryClient.invalidateQueries({
      queryKey: CURRENT_USER_PROFILE_QUERY_KEY,
    });
  };

  return useMutation<ProfileUpdatePayload, ApiError, ProfileUpdateVariables>({
    mutationFn,
    onSuccess,
  });
};

export const useUpdateProfileContext = () => {
  const queryClient = useQueryClient();

  const mutationFn = (context: ContextSlug) => {
    return client.context.update(context);
  };

  const onSuccess = () => {
    // return invalidateQueries promise to let mutation in pending state
    // until all the related queries have finish fetching new data.
    const queryKey = CURRENT_USER_PROFILE_QUERY_KEY;
    return queryClient.invalidateQueries({ queryKey });
  };

  return useMutation<null, ApiError, ContextSlug>({
    mutationKey: UPDATE_CONTEXT_MUTATION_KEY,
    mutationFn,
    onSuccess,
  });
};

export const useIsProfileContextMutating = () => {
  const mutationKey = UPDATE_CONTEXT_MUTATION_KEY;
  const mutationCount = useIsMutating({ mutationKey });

  return mutationCount > 0;
};
